import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { httpRequest } from '@/lib/http-client';

/** A display signal only: the API verifies the role again on every admin request. */
export function useServerRole() {
  const { isLoaded, isSignedIn, userId, sessionId, getToken } = useAuth();
  const query = useQuery({
    queryKey: ['server-role', userId, sessionId],
    enabled: Boolean(isLoaded && isSignedIn && userId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      const result = await httpRequest<{ role: string; hasCompany?: boolean }>('/api/access', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (result.role !== 'admin' && result.role !== 'user') {
        throw new Error('Unable to verify access');
      }
      // An older server omits hasCompany; treat that as unknown, not "no company".
      // The API still enforces company access on every order request.
      return {
        role: result.role,
        hasCompany: typeof result.hasCompany === 'boolean' ? result.hasCompany : undefined,
      };
    },
  });
  return {
    role: isSignedIn && !query.isError ? query.data?.role : undefined,
    hasCompany: isSignedIn && !query.isError ? query.data?.hasCompany : undefined,
    isLoading: !isLoaded || Boolean(isSignedIn && query.isPending),
    error: query.error,
    retry: query.refetch,
  };
}
